import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { notifyError } from '../../lib/errors';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Database, Download, FileJson, Loader2, UploadCloud, XCircle } from 'lucide-react';
import { opsApi, apiError, fmtNum, FONT_HEAD, GRAD } from './opsApi';
import OpsQBQuestions from './OpsQBQuestions';

// QB Management — super admin uploads an exam-prep question bank (JSON).
// The backend validates each record against the exam's published syllabus,
// embeds it into the local vector index and publishes it to the shared bank
// every institute's Exam Prep reads from (backend/exam_qb_import.py).

const MAX_FILE_BYTES = 20 * 1024 * 1024;
const POLL_MS = 2000;

const SAMPLE_RECORDS = [
  {
    source_ref: 'jee-main-2026-phy-001', exam: 'JEE_MAIN', year: 2026,
    subject_name: 'Physics', chapter_name: 'Thermodynamics',
    difficulty: 'medium', question_type: 'mcq',
    question_latex: 'An ideal gas expands isothermally. The change in internal energy $\\Delta U$ is:',
    options_latex: ['Positive', 'Negative', 'Zero', 'Depends on pressure'],
    correct_answer: 'C', solution_latex: 'For an ideal gas $U$ depends only on $T$, so $\\Delta U = 0$.',
  },
  {
    source_ref: 'jee-main-2026-phy-002', exam: 'JEE_MAIN', year: 2026,
    subject_name: 'Physics', chapter_name: 'Thermodynamics',
    difficulty: 'easy', question_type: 'numerical',
    question_latex: 'A gas absorbs $500\\,J$ of heat and does $200\\,J$ of work. Find $\\Delta U$ in joules.',
    options_latex: null, correct_answer: '300', solution_latex: '$\\Delta U = Q - W = 300\\,J$.',
  },
];

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true }); }
  catch { return iso || '—'; }
};

const downloadSample = () => {
  const blob = new Blob([JSON.stringify(SAMPLE_RECORDS, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: 'question-bank-sample.json' });
  a.click();
  URL.revokeObjectURL(url);
};

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 text-xs text-slate-400 uppercase tracking-wide"><Icon className="w-3.5 h-3.5" />{label}</div>
      <div className="text-2xl font-semibold mt-1" style={FONT_HEAD}>{value}</div>
    </div>
  );
}

function ErrorTable({ errors, total }) {
  if (!errors?.length) return null;
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold text-red-600 mb-2">
        {fmtNum(total)} record{total === 1 ? '' : 's'} skipped{total > errors.length ? ` (first ${errors.length} shown)` : ''}
      </p>
      <div className="max-h-64 overflow-auto rounded-xl border border-red-100">
        <table className="w-full text-xs">
          <thead className="bg-red-50 text-red-700 sticky top-0">
            <tr><th className="px-3 py-2 text-left font-medium">#</th><th className="px-3 py-2 text-left font-medium">source_ref</th><th className="px-3 py-2 text-left font-medium">Problem</th></tr>
          </thead>
          <tbody>
            {errors.map((e, i) => (
              <tr key={`${e.index}-${i}`} className="border-t border-red-50">
                <td className="px-3 py-1.5 text-slate-400">{e.index != null ? e.index + 1 : '—'}</td>
                <td className="px-3 py-1.5 font-mono">{e.source_ref || '—'}</td>
                <td className="px-3 py-1.5 text-slate-600">{e.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OpsQBManagement() {
  const [summary, setSummary] = useState(null);
  const [file, setFile] = useState(null);           // { name, records }
  const [validation, setValidation] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const loadSummary = useCallback(() => {
    opsApi.get('/qb/summary').then((r) => setSummary(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Poll the running import until it finishes.
  useEffect(() => {
    if (!job || !['queued', 'running'].includes(job.status)) return undefined;
    const timer = setTimeout(() => {
      opsApi.get(`/qb/imports/${job.id}`).then((r) => {
        setJob(r.data);
        if (r.data.status === 'completed') { toast.success(`${fmtNum(r.data.ingested)} questions published`); loadSummary(); }
        if (r.data.status === 'failed') notifyError(r.data.failure, 'Import failed');
      }).catch((e) => toast.error(apiError(e)));
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [job, loadSummary]);

  const pickFile = async (picked) => {
    setValidation(null); setJob(null);
    if (!picked) return;
    if (picked.size > MAX_FILE_BYTES) { toast.error('File is larger than 20 MB — split it into smaller files'); return; }
    try {
      const records = JSON.parse(await picked.text());
      if (!Array.isArray(records)) throw new Error('The file must contain a JSON list of questions');
      setFile({ name: picked.name, records });
    } catch (e) {
      setFile(null);
      if (e instanceof SyntaxError) toast.error('Not valid JSON — check the file format'); else notifyError(e, 'Import failed');
    }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await opsApi.post('/qb/validate', { filename: file.name, records: file.records });
      setValidation(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const startImport = async () => {
    setBusy(true);
    try {
      const { data } = await opsApi.post('/qb/imports', { filename: file.name, records: file.records });
      setJob(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const reset = () => { setFile(null); setValidation(null); setJob(null); if (inputRef.current) inputRef.current.value = ''; };
  const running = job && ['queued', 'running'].includes(job.status);
  const pct = job?.valid ? Math.round((job.processed / job.valid) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="ops-qb-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={FONT_HEAD}>QB Management</h1>
        <p className="text-sm text-slate-500 mt-1">Upload exam-prep question banks. Imported questions are added to the local vector index and published to Exam Prep for every institute.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Published questions" value={summary ? fmtNum(summary.total_published) : '—'} icon={CheckCircle2} />
        <Stat label="Vector index" value={summary?.vector_index_count != null ? fmtNum(summary.vector_index_count) : '—'} icon={Database} />
        <Stat label="Exams with syllabus" value={summary ? summary.syllabi.length : '—'} icon={FileJson} />
      </div>

      {/* Upload */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-semibold" style={FONT_HEAD}>Upload question bank</h2>
          <button onClick={downloadSample} className="text-xs font-medium flex items-center gap-1 text-[#34A853] hover:underline" data-testid="qb-download-sample">
            <Download className="w-3.5 h-3.5" /> Download sample JSON
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          A JSON list of questions. Each needs <code>source_ref, exam, year, subject_name, chapter_name, difficulty, question_type, question_latex, options_latex, correct_answer</code>; <code>solution_latex</code> is optional.
          Subject and chapter must match a published syllabus
          {summary?.syllabi?.length ? <>: {summary.syllabi.map((s) => `${s.exam.replace('_', ' ')} ${s.year}`).join(', ')}</> : ''}.
        </p>

        <label
          className="mt-4 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-2xl py-8 cursor-pointer hover:border-[#34A853] transition-colors"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
          data-testid="qb-dropzone">
          <UploadCloud className="w-8 h-8 text-slate-300" />
          {file
            ? <span className="text-sm font-medium">{file.name} · {fmtNum(file.records.length)} records</span>
            : <span className="text-sm text-slate-500">Drop a <b>.json</b> file here, or click to choose</span>}
          <input ref={inputRef} type="file" accept=".json,application/json" className="hidden"
            onChange={(e) => pickFile(e.target.files?.[0])} data-testid="qb-file-input" />
        </label>

        {file && (
          <div className="flex gap-2 mt-4 flex-wrap">
            <Button onClick={validate} disabled={busy || running} variant="outline" className="rounded-xl" data-testid="qb-validate-btn">
              {busy && !validation ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Validate
            </Button>
            <Button onClick={startImport} disabled={busy || running || !validation?.valid}
              className="rounded-xl text-white border-0" style={{ background: GRAD }} data-testid="qb-import-btn">
              Import &amp; publish{validation?.valid ? ` ${fmtNum(validation.valid)}` : ''}
            </Button>
            <Button onClick={reset} disabled={running} variant="ghost" className="rounded-xl">Clear</Button>
          </div>
        )}

        {validation && !job && (
          <div className="mt-4 text-sm" data-testid="qb-validation">
            <div className="flex gap-4 flex-wrap">
              <span className="flex items-center gap-1 text-green-700"><CheckCircle2 className="w-4 h-4" /> {fmtNum(validation.valid)} ready</span>
              {validation.error_count > 0 && <span className="flex items-center gap-1 text-red-600"><XCircle className="w-4 h-4" /> {fmtNum(validation.error_count)} with problems</span>}
            </div>
            {validation.by_exam_year.length > 0 && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {validation.by_exam_year.map((g) => (
                  <span key={`${g.exam}-${g.year}`} className="px-2 py-0.5 rounded-md text-xs bg-green-50 text-green-700 border border-green-100">{g.exam.replace('_', ' ')} {g.year}: {fmtNum(g.count)}</span>
                ))}
              </div>
            )}
            <ErrorTable errors={validation.errors} total={validation.error_count} />
          </div>
        )}

        {job && (
          <div className="mt-4" data-testid="qb-job">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">
                {running ? 'Embedding and publishing…' : job.status === 'completed' ? 'Import complete' : 'Import failed'}
              </span>
              <span className="text-slate-500">{fmtNum(job.processed)} / {fmtNum(job.valid)}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 mt-2 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${running ? pct : 100}%`, background: job.status === 'failed' ? '#ef4444' : GRAD }} />
            </div>
            {job.status === 'completed' && (
              <p className="text-sm text-green-700 mt-2">{fmtNum(job.ingested)} questions published and added to the vector index.</p>
            )}
            {job.status === 'failed' && <p className="text-sm text-red-600 mt-2">{job.failure}</p>}
            {!running && <ErrorTable errors={job.errors} total={job.error_count} />}
          </div>
        )}
      </div>

      {/* Banks */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100"><h2 className="font-semibold" style={FONT_HEAD}>Question banks</h2></div>
        {!summary ? (
          <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#34A853]" /></div>
        ) : summary.banks.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-10">No questions yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
              <th className="px-5 py-3 font-medium">Exam</th><th className="px-4 py-3 font-medium">Year</th>
              <th className="px-4 py-3 font-medium text-right">Published</th><th className="px-4 py-3 font-medium text-right">Draft</th><th className="px-5 py-3 font-medium text-right">Flagged</th>
            </tr></thead>
            <tbody>
              {summary.banks.map((b) => (
                <tr key={`${b.exam}-${b.year}`} className="border-b border-slate-50">
                  <td className="px-5 py-3 font-medium">{String(b.exam).replace('_', ' ')}</td>
                  <td className="px-4 py-3">{b.year}</td>
                  <td className="px-4 py-3 text-right text-green-700">{fmtNum(b.published)}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{fmtNum(b.draft)}</td>
                  <td className="px-5 py-3 text-right text-amber-600">{fmtNum(b.flagged)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {summary && (
        <OpsQBQuestions
          exams={summary.allowed.exams}
          years={[...new Set(summary.banks.map((b) => b.year))].sort((a, b) => b - a)}
          onChanged={loadSummary}
        />
      )}

      {/* Recent imports */}
      {summary?.recent_imports?.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100"><h2 className="font-semibold" style={FONT_HEAD}>Recent imports</h2></div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400 uppercase tracking-wide">
              <th className="px-5 py-3 font-medium">When</th><th className="px-4 py-3 font-medium">File</th><th className="px-4 py-3 font-medium">By</th>
              <th className="px-4 py-3 font-medium">Status</th><th className="px-5 py-3 font-medium text-right">Published / records</th>
            </tr></thead>
            <tbody>
              {summary.recent_imports.map((j) => (
                <tr key={j.id} className="border-b border-slate-50">
                  <td className="px-5 py-3 text-xs text-slate-400 whitespace-nowrap">{fmtWhen(j.created_at)}</td>
                  <td className="px-4 py-3 text-xs font-mono truncate max-w-[220px]">{j.filename}</td>
                  <td className="px-4 py-3 text-xs">{j.created_by_name}</td>
                  <td className="px-4 py-3 text-xs capitalize">{j.status}</td>
                  <td className="px-5 py-3 text-xs text-right">{fmtNum(j.ingested)} / {fmtNum(j.total)}{j.error_count ? <span className="text-red-500"> · {fmtNum(j.error_count)} skipped</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
